/**
 * VariationManager — admin sub-component for managing a single ShopItem's variations.
 *
 * Renders an inline list of variations under the AdminShop edit dialog with:
 *  - Add a new variation (name, SKU, optional price override, inventory)
 *  - Edit existing variations inline (commits on blur)
 *  - Toggle active (without deleting)
 *  - Delete variation
 *  - Stock-status pill per row (In/Low/Out)
 *
 * Notifies parent via onChange() so the parent list can re-fetch items.
 */
import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import { Input } from "./../../components/ui/input";
import { Label } from "./../../components/ui/label";

const EMPTY = { name: "", sku: "", price: "", inventory: "0" };

const stockState = (inv, threshold) => {
  const n = Number(inv || 0);
  if (n <= 0) return { kind: "out", label: "Out" };
  if (n <= Number(threshold || 5)) return { kind: "low", label: "Low" };
  return { kind: "in", label: "In Stock" };
};

const badgeClass = (kind) =>
  ({
    in: "border-demolition-chrome/40 text-demolition-chrome",
    low: "border-yellow-500/50 text-yellow-300",
    out: "border-red-500/50 text-red-400",
  }[kind]);

export default function VariationManager({ itemId, lowStockThreshold, onChange }) {
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch the product fresh so we have its current variations
        const all = await api.getAdminShopItems();
        if (cancelled) return;
        const item = all.find((i) => i.id === itemId);
        const v = (item?.variations || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setVariations(v);
      } catch (err) {
        logger.warn("Variation fetch failed:", err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const reload = async () => {
    const all = await api.getAdminShopItems();
    const item = all.find((i) => i.id === itemId);
    const v = (item?.variations || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setVariations(v);
    onChange?.();
  };

  const parsePrice = (s) => {
    const t = (s || "").trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : NaN;
  };

  const handleAdd = async () => {
    const name = draft.name.trim();
    if (!name) {
      toast.error("Variation name is required");
      return;
    }
    const price = parsePrice(draft.price);
    if (Number.isNaN(price)) {
      toast.error("Price must be a number or blank");
      return;
    }
    const inv = parseInt(draft.inventory, 10);
    if (Number.isNaN(inv) || inv < 0) {
      toast.error("Inventory must be a non-negative number");
      return;
    }
    setBusy(true);
    try {
      await api.addVariation(itemId, {
        name,
        sku: draft.sku.trim() || null,
        price,
        inventory: inv,
        active: true,
      });
      setDraft(EMPTY);
      await reload();
      toast.success("Variation added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add variation");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveRow = async (v, patch) => {
    setBusy(true);
    try {
      await api.updateVariation(itemId, v.id, patch);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete variation "${v.name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteVariation(itemId, v.id);
      await reload();
      toast.success("Variation deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-white/10 pt-4" data-testid="variation-manager">
      <div className="flex items-center justify-between">
        <Label className="font-teko text-lg text-white uppercase tracking-wider">
          Variations &amp; Inventory
        </Label>
        <span className="font-mono text-xs text-white/40">
          {variations.length} {variations.length === 1 ? "variation" : "variations"}
        </span>
      </div>

      {loading ? (
        <p className="font-mono text-sm text-white/40">Loading...</p>
      ) : (
        <div className="space-y-2">
          {variations.map((v) => {
            const stock = stockState(v.inventory, lowStockThreshold);
            return (
              <VariationRow
                key={v.id}
                variation={v}
                stockBadge={
                  <span
                    className={`font-mono text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${badgeClass(stock.kind)}`}
                  >
                    {stock.label}
                  </span>
                }
                onSave={(patch) => handleSaveRow(v, patch)}
                onDelete={() => handleDelete(v)}
                disabled={busy}
              />
            );
          })}
          {variations.length === 0 && (
            <p className="font-mono text-xs text-white/40 italic px-3 py-2 border border-dashed border-white/10">
              No variations yet. Add one below — e.g. &quot;Small Black&quot;, &quot;Signed&quot;, etc. While there are no variations, the product behaves as a single-SKU item.
            </p>
          )}
        </div>
      )}

      {/* Add new */}
      <div className="grid grid-cols-12 gap-2 p-3 bg-demolition-subtle border border-white/10" data-testid="variation-add-row">
        <Input
          placeholder="Name (e.g. Small Black)"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="col-span-4 bg-demolition-paper border-white/10 text-white font-mono text-sm"
          data-testid="variation-name-input"
        />
        <Input
          placeholder="SKU (optional)"
          value={draft.sku}
          onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
          className="col-span-3 bg-demolition-paper border-white/10 text-white font-mono text-sm"
          data-testid="variation-sku-input"
        />
        <Input
          type="number"
          step="0.01"
          min="0"
          placeholder="Price (override)"
          value={draft.price}
          onChange={(e) => setDraft({ ...draft, price: e.target.value })}
          className="col-span-2 bg-demolition-paper border-white/10 text-white font-mono text-sm"
          data-testid="variation-price-input"
        />
        <Input
          type="number"
          min="0"
          step="1"
          placeholder="Qty"
          value={draft.inventory}
          onChange={(e) => setDraft({ ...draft, inventory: e.target.value })}
          className="col-span-2 bg-demolition-paper border-white/10 text-white font-mono text-sm"
          data-testid="variation-inventory-input"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={busy}
          className="col-span-1 flex items-center justify-center bg-demolition-red hover:bg-demolition-red/85 text-white p-2 transition-colors disabled:opacity-50"
          aria-label="Add variation"
          data-testid="variation-add-btn"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function VariationRow({ variation, stockBadge, onSave, onDelete, disabled }) {
  const [name, setName] = useState(variation.name || "");
  const [sku, setSku] = useState(variation.sku || "");
  const [price, setPrice] = useState(variation.price != null ? String(variation.price) : "");
  const [inventory, setInventory] = useState(String(variation.inventory ?? 0));
  const [active, setActive] = useState(variation.active !== false);

  useEffect(() => {
    setName(variation.name || "");
    setSku(variation.sku || "");
    setPrice(variation.price != null ? String(variation.price) : "");
    setInventory(String(variation.inventory ?? 0));
    setActive(variation.active !== false);
  }, [variation]);

  const dirty =
    name !== (variation.name || "") ||
    sku !== (variation.sku || "") ||
    price !== (variation.price != null ? String(variation.price) : "") ||
    inventory !== String(variation.inventory ?? 0) ||
    active !== (variation.active !== false);

  const handleSave = () => {
    const patch = {
      name: name.trim(),
      sku: sku.trim() || null,
      price: price.trim() === "" ? null : Math.round(Number(price) * 100) / 100,
      inventory: Math.max(0, parseInt(inventory, 10) || 0),
      active,
    };
    onSave(patch);
  };

  return (
    <div
      className={`grid grid-cols-12 gap-2 p-2 border ${active ? "border-white/10" : "border-white/5 opacity-60"} bg-demolition-paper`}
      data-testid={`variation-row-${variation.id}`}
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="col-span-3 bg-demolition-subtle border-white/10 text-white font-mono text-sm"
        data-testid={`variation-name-${variation.id}`}
      />
      <Input
        value={sku}
        onChange={(e) => setSku(e.target.value)}
        placeholder="SKU"
        className="col-span-3 bg-demolition-subtle border-white/10 text-white font-mono text-sm"
        data-testid={`variation-sku-${variation.id}`}
      />
      <Input
        type="number"
        step="0.01"
        min="0"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        placeholder="Inherit"
        className="col-span-2 bg-demolition-subtle border-white/10 text-white font-mono text-sm"
        data-testid={`variation-price-${variation.id}`}
      />
      <Input
        type="number"
        min="0"
        step="1"
        value={inventory}
        onChange={(e) => setInventory(e.target.value)}
        className="col-span-1 bg-demolition-subtle border-white/10 text-white font-mono text-sm"
        data-testid={`variation-inventory-${variation.id}`}
      />
      <div className="col-span-3 flex items-center justify-end gap-1">
        {stockBadge}
        <button
          type="button"
          onClick={() => setActive((v) => !v)}
          className={`p-1.5 ${active ? "text-white/70 hover:text-white" : "text-white/30 hover:text-white/60"} transition-colors`}
          title={active ? "Active — click to hide" : "Hidden — click to show"}
          aria-label={active ? "Deactivate variation" : "Activate variation"}
          data-testid={`variation-active-${variation.id}`}
        >
          {active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || disabled}
          className="p-1.5 text-demolition-red hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Save changes"
          data-testid={`variation-save-${variation.id}`}
        >
          <Save className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          className="p-1.5 text-white/40 hover:text-demolition-red transition-colors"
          aria-label="Delete variation"
          data-testid={`variation-delete-${variation.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
